/**
 * useIntakeTemplate Hook
 *
 * Fetches and manages intake flow templates from system templates and API.
 * Supports both static system templates and custom templates created by businesses.
 * Provides template data with type safety and error handling.
 *
 * Issue #432 - Templated Loan Intake Wizard
 */

import { baseColors } from '@ops-matrix/design-system-ui';
import {
  getIntakeFlowTemplateById,
  getIntakeFlowTemplateByType,
  type IntakeFlowTemplate,
  type IntakeFlowType,
  type TemplateComplexity,
  type TemplateIndustry,
} from '@ops-matrix/shared-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntakeTemplateById, useIntakeTemplatesForBusiness } from '../lib/trpc/hooks';

export interface UseIntakeTemplateOptions {
  /** Template ID to load (can be system or custom) */
  templateId?: string;
  /** Template type to load (alternative to templateId) */
  templateType?: IntakeFlowType;
  /** Business ID for loading custom templates */
  businessId?: string;
  /** Whether to auto-load the template */
  enabled?: boolean;
}

export interface UseIntakeTemplateResult {
  /** The loaded template */
  template: IntakeFlowTemplate | null;
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Reload the template */
  refetch: () => void;
  /** Get a specific template by ID (system templates only) */
  getTemplateById: (id: string) => IntakeFlowTemplate | undefined;
  /** Get a specific template by type (system templates only) */
  getTemplateByType: (type: IntakeFlowType) => IntakeFlowTemplate | undefined;
}

/**
 * Hook for fetching intake flow templates
 *
 * Supports:
 * - System templates (loaded from static template library)
 * - Custom templates (loaded from API by templateId or businessId)
 *
 * @example
 * ```tsx
 * // By type (most common) - loads system template
 * const { template, isLoading } = useIntakeTemplate({ templateType: 'bridge' });
 *
 * // By ID - checks API first, then falls back to system templates
 * const { template } = useIntakeTemplate({ templateId: 'tpl-intake-bridge-fix-flip' });
 *
 * // With business context - can load custom templates for that business
 * const { template } = useIntakeTemplate({
 *   businessId: 'biz-123',
 *   templateType: 'bridge',
 * });
 * ```
 */
export function useIntakeTemplate(options: UseIntakeTemplateOptions = {}): UseIntakeTemplateResult {
  const { templateId, templateType, businessId, enabled = true } = options;

  const [template, setTemplate] = useState<IntakeFlowTemplate | null>(null);
  const [localError, setLocalError] = useState<Error | null>(null);

  // API queries for custom templates
  // Query by templateId - check if it's a custom template (requires businessId)
  const customTemplateQuery = useIntakeTemplateById(
    enabled && templateId && !templateId.startsWith('tpl-intake-') && businessId ? businessId : null,
    enabled && templateId && !templateId.startsWith('tpl-intake-') && businessId ? templateId : null,
  );

  // Query by business + type - get custom templates for a business
  const businessTemplatesQuery = useIntakeTemplatesForBusiness(
    enabled && !templateId && businessId ? businessId : null,
    templateType,
  );

  // Memoize the getter functions for system templates
  const getTemplateById = useCallback((id: string) => {
    return getIntakeFlowTemplateById(id);
  }, []);

  const getTemplateByType = useCallback((type: IntakeFlowType) => {
    return getIntakeFlowTemplateByType(type);
  }, []);

  // Helper to convert API response to IntakeFlowTemplate with validation
  const convertToTemplate = useCallback(
    (apiTemplate: NonNullable<typeof customTemplateQuery.data>): IntakeFlowTemplate | null => {
      // Validate required fields
      if (!apiTemplate.id || !apiTemplate.name) {
        console.warn('Invalid template: missing id or name', apiTemplate);
        return null;
      }

      // Validate data structure
      const data = apiTemplate.data;
      if (!data || typeof data !== 'object') {
        console.warn('Invalid template: missing or invalid data', apiTemplate.id);
        return null;
      }

      // Validate required data fields
      const templateData = data as Record<string, unknown>;
      if (!templateData.intakeType || !Array.isArray(templateData.steps)) {
        console.warn('Invalid template data: missing intakeType or steps', apiTemplate.id);
        return null;
      }

      return {
        id: apiTemplate.id,
        name: apiTemplate.name,
        description: apiTemplate.description || '',
        category: apiTemplate.category || 'custom',
        icon: apiTemplate.icon || 'Assignment',
        color: apiTemplate.color || baseColors.info[500],
        tags: apiTemplate.tags || [],
        complexity: (apiTemplate.complexity || 'intermediate') as TemplateComplexity,
        industryFit: (apiTemplate.industryFit || []) as TemplateIndustry[],
        data: apiTemplate.data,
      };
    },
    [],
  );

  // Load template when options or API data changes
  useEffect(() => {
    if (!enabled) return;

    setLocalError(null);

    // Priority 1: Check if we have a custom template from API by templateId
    if (templateId && customTemplateQuery.data) {
      const converted = convertToTemplate(customTemplateQuery.data);
      if (converted) {
        setTemplate(converted);
        return;
      }
      // Invalid template data, fall through to system templates
    }

    // Priority 2: Check if we have custom templates for the business matching the type
    if (businessId && businessTemplatesQuery.data && businessTemplatesQuery.data.length > 0) {
      // If templateType is specified, filter by it
      if (templateType) {
        const matchingTemplate = businessTemplatesQuery.data.find(
          (t: NonNullable<typeof customTemplateQuery.data>) => t.data?.intakeType === templateType,
        );
        if (matchingTemplate) {
          const converted = convertToTemplate(matchingTemplate);
          if (converted) {
            setTemplate(converted);
            return;
          }
        }
      } else {
        // Use first valid custom template if no type specified
        for (const t of businessTemplatesQuery.data) {
          const converted = convertToTemplate(t);
          if (converted) {
            setTemplate(converted);
            return;
          }
        }
      }
    }

    // Priority 3: Fall back to system templates
    let loadedTemplate: IntakeFlowTemplate | undefined;

    if (templateId) {
      // Try system template by ID
      loadedTemplate = getIntakeFlowTemplateById(templateId);
    }
    // If templateId didn't match, try by type (DB may store different IDs than system templates)
    if (!loadedTemplate && templateType) {
      loadedTemplate = getIntakeFlowTemplateByType(templateType);
    }

    if (loadedTemplate) {
      setTemplate(loadedTemplate);
    } else if (templateId || templateType) {
      // Only set error if we were trying to load a specific template and all sources failed
      const stillLoading = customTemplateQuery.isLoading || businessTemplatesQuery.isLoading;
      if (!stillLoading) {
        setLocalError(new Error(`Template not found: ${templateId || templateType}`));
        setTemplate(null);
      }
    }
  }, [
    enabled,
    templateId,
    templateType,
    businessId,
    customTemplateQuery.data,
    customTemplateQuery.isLoading,
    businessTemplatesQuery.data,
    businessTemplatesQuery.isLoading,
  ]);

  // Combined loading state
  const isLoading = customTemplateQuery.isLoading || businessTemplatesQuery.isLoading;

  // Combined error state
  const error = localError || customTemplateQuery.error || businessTemplatesQuery.error || null;

  // Refetch function
  const refetch = useCallback(() => {
    if (customTemplateQuery.refetch) {
      void customTemplateQuery.refetch();
    }
    if (businessTemplatesQuery.refetch) {
      void businessTemplatesQuery.refetch();
    }
  }, [customTemplateQuery, businessTemplatesQuery]);

  return useMemo(
    () => ({
      template,
      isLoading,
      error: error instanceof Error ? error : error ? new Error(String(error)) : null,
      refetch,
      getTemplateById,
      getTemplateByType,
    }),
    [template, isLoading, error, refetch, getTemplateById, getTemplateByType],
  );
}

export default useIntakeTemplate;
